import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { RuteroConnectorsList } from '@frontend/features/rutero/components/RuteroConnectorsList';

// Keys como string literal (no el enum de @bandido/shared), mismo criterio
// que TravelIntelligenceSettings: el backend las lee vía SettingsFields.
const RUTERO_ENABLED_KEY = 'RUTERO_ENABLED';
const VOUCHER_AUTO_EMAIL_KEY = 'VOUCHER_AUTO_EMAIL';

export const RuteroView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const agencyId = Number(id);
  const { data: settings } = useGetSettingsByAgencyId({ id });
  const [saving, setSaving] = useState(false);

  const enabled = settings?.[RUTERO_ENABLED_KEY] === 'true';
  const voucherAutoEmail = settings?.[VOUCHER_AUTO_EMAIL_KEY] === 'true';

  const toggleSetting = async (key: string, value: boolean) => {
    setSaving(true);
    try {
      await client.post(`settings/agency/${agencyId}`, {
        [key]: value ? 'true' : 'false',
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.getSettingsByAgencyId, id],
      });
      addToast({ message: t('rutero.saveSuccess'), color: 'success' });
    } catch {
      addToast({ message: t('rutero.saveError'), color: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: '24px',
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(1, 3),
      }}
    >
      <OverlayScrollbarsComponent
        style={{
          marginTop: theme.spacing(5),
          boxSizing: 'border-box',
          width: '100%',
          overflowY: 'hidden',
          height: 'calc(100vh - 160px)',
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" gap={4}>
          <Box>
            <Typography variant="h5">{t('rutero.settingsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('rutero.pageIntro')}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                disabled={saving || settings === undefined}
                onChange={(event) =>
                  toggleSetting(RUTERO_ENABLED_KEY, event.target.checked)
                }
              />
            }
            label={t('rutero.enabledLabel')}
          />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={voucherAutoEmail}
                  // Sin servicios de tierra no hay reservas que confirmar,
                  // así que no se puede PRENDER; apagarlo sí, siempre: si no,
                  // quedaba encendido y sin forma de sacarlo desde acá.
                  disabled={
                    saving ||
                    settings === undefined ||
                    (!enabled && !voucherAutoEmail)
                  }
                  onChange={(event) =>
                    toggleSetting(VOUCHER_AUTO_EMAIL_KEY, event.target.checked)
                  }
                />
              }
              label={t('rutero.voucherAutoEmailLabel')}
            />
            <Typography variant="body2" color="text.secondary">
              {t('rutero.voucherAutoEmailHint')}
            </Typography>
          </Box>

          <Divider />
          <RuteroConnectorsList agencyId={agencyId} />
        </Box>
      </OverlayScrollbarsComponent>
    </Box>
  );
};

export default RuteroView;
